import { motion } from 'framer-motion'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { ContainerPage } from '@/components/common'
import { SubTitle } from '@/components/common/UI'
import { PasswordControl } from '@/components/form-controls'
import { Button, Card, CardBody, CardHeader, Image } from '@/components/nextui'
import { useAuthApi } from '@/services/api/authApi'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'

interface ChangePasswordForm {
    newPassword: string
    passwordConfirmation: string
}

export const ChangePassword = (): JSX.Element => {
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const token = searchParams.get('token')
    const navigate = useNavigate()
    const { control, handleSubmit, watch } = useForm<ChangePasswordForm>()
    const { changePassword } = useAuthApi({
        errorHandling: ErrorHandlingStrategy.RETURN,
        messageLevel: MessageLevel.ALL
    })
    const [successMessage, setSuccessMessage] = useState<string | null>(null)

    const password = watch('newPassword')

    const onSubmit = async (data: ChangePasswordForm) => {
        if (!token) {
            return
        }

        try {
            const response = await changePassword(
                token,
                data.newPassword,
                data.passwordConfirmation
            )

            if (response.status === 200) {
                setSuccessMessage(t('passwordChange.success'))
                setTimeout(() => {
                    navigate('/login')
                }, 3000)
            }
        } catch (error) {
            console.error('Password change error:', error)
        }
    }

    return (
        <ContainerPage>
            <article className="p-4 bg-gradient-ean-2 dark:bg-gradient-ean">
                <motion.div
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 2, ease: 'easeOut' }}
                    className="flex flex-col justify-center items-center"
                >
                    <Card className="w-full max-w-md lg:max-w-4xl backdrop-blur-md mx-auto my-4 bg-white/90 dark:bg-gray-800/90">
                        <CardHeader className="flex flex-col gap-4 justify-center items-center">
                            <Image
                                src="/LogoEAN.jpg"
                                alt="logo"
                                className="w-[150px]"
                            />
                            <SubTitle
                                className="font-normal text-center"
                                subTitle={t('passwordChange.title')}
                            />
                            {successMessage && (
                                <p className="text-success">{successMessage}</p>
                            )}
                        </CardHeader>
                        <CardBody className="flex flex-col gap-4">
                            <form
                                onSubmit={handleSubmit(onSubmit)}
                                className="space-y-6 lg:mx-auto lg:my-0 lg:w-4/5"
                            >
                                <PasswordControl
                                    name="newPassword"
                                    control={control}
                                    label={t('passwordChange.newPassword')}
                                    rules={{
                                        required: t(
                                            'forms.common.required.passwordRequired'
                                        )
                                    }}
                                />
                                <PasswordControl
                                    showStrength={false}
                                    name="passwordConfirmation"
                                    control={control}
                                    label={t('passwordChange.confirmPassword')}
                                    rules={{
                                        required: t(
                                            'forms.common.required.passwordRequired'
                                        ),
                                        validate: (value) =>
                                            value === password ||
                                            t(
                                                'createAccount.passwordsMustMatch'
                                            )
                                    }}
                                />
                                <div className="flex justify-evenly items-center">
                                    <Button
                                        type="submit"
                                        color="primary"
                                        className="w-full"
                                    >
                                        {t('passwordChange.submit')}
                                    </Button>
                                </div>
                            </form>
                            <Link
                                to="/login"
                                className="text-xs font-light text-center underline text-disabled"
                            >
                                {t('passwordChange.backToLogin')}
                            </Link>
                        </CardBody>
                    </Card>
                </motion.div>
            </article>
        </ContainerPage>
    )
}
